import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { type ReactNode, useEffect, useState } from 'react'
import { useAccount, WagmiProvider } from 'wagmi'
import type { Config } from 'wagmi'
import { lumi } from '@/lib/lumi'
import { WalletAddressContext, WalletReadyContext } from '@/lib/walletContext'

const queryClient = new QueryClient()

/**
 * Re-exported for backwards compatibility: existing components import
 * these contexts from this file. The actual `createContext` calls live in
 * `@/lib/walletContext` (a wagmi-free module) so the prerender entry can
 * use `WalletProviderStatic` without ever loading `wagmi`/AppKit.
 */
export { WalletAddressContext, WalletReadyContext }

function WalletAddressScope({ children }: { children: ReactNode }) {
  const { address } = useAccount()
  return (
    <WalletAddressContext.Provider value={address}>
      {children}
    </WalletAddressContext.Provider>
  )
}

type WalletProviderProps = {
  children: ReactNode
}

const REOWN_ID_KEY = 'pp.reownProjectId'

function isReownProjectId(value: string) {
  return /^[a-f0-9]{32}$/i.test(value)
}

function readCachedProjectId() {
  try {
    const cached = (localStorage.getItem(REOWN_ID_KEY) || '').trim()
    return isReownProjectId(cached) ? cached : ''
  }
  catch {
    return ''
  }
}

function rememberProjectId(projectId: string) {
  if (!isReownProjectId(projectId)) return
  try {
    localStorage.setItem(REOWN_ID_KEY, projectId)
  }
  catch {
    // Private browsing can reject storage. The button still works for this visit.
  }
}

async function fetchReownProjectId() {
  const result = await lumi.functions.invoke('reownConfig', {
    method: 'POST',
  }) as { projectId?: string }
  return (result?.projectId || '').trim()
}

async function loadReownProjectId() {
  const fromEnv = (import.meta.env.VITE_REOWN_PROJECT_ID || '').trim()
  if (isReownProjectId(fromEnv)) return fromEnv

  const cached = readCachedProjectId()
  if (cached) return cached

  try {
    return await fetchReownProjectId()
  }
  catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'reownConfig failed'
    console.error(JSON.stringify({
      stage: 'reown_config_error',
      message,
    }))
  }
  return ''
}

function WalletProvider({ children }: WalletProviderProps) {
  const [wagmiConfig, setWagmiConfig] = useState<Config | null>(null)

  useEffect(() => {
    let cancelled = false

    const boot = async () => {
      try {
        const projectId = await loadReownProjectId()
        if (!projectId || cancelled) return
        rememberProjectId(projectId)
        const { initAppKit } = await import('@/lib/appkit')
        if (cancelled) return
        setWagmiConfig(initAppKit(projectId).wagmiConfig)
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to initialize wallet'
        console.error(JSON.stringify({
          stage: 'wallet_init_error',
          message,
        }))
      }
    }

    const start = () => {
      if (!cancelled) void boot()
    }
    const idleId = window.requestIdleCallback?.(start, { timeout: 1500 })
    const timer = idleId === undefined ? window.setTimeout(start, 300) : undefined

    return () => {
      cancelled = true
      if (idleId !== undefined) window.cancelIdleCallback(idleId)
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [])

  if (!wagmiConfig) {
    return (
      <WalletReadyContext.Provider value={false}>
        {children}
      </WalletReadyContext.Provider>
    )
  }

  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <WalletReadyContext.Provider value={true}>
          <WalletAddressScope>
            {children}
          </WalletAddressScope>
        </WalletReadyContext.Provider>
      </QueryClientProvider>
    </WagmiProvider>
  )
}

export default WalletProvider
